import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { compareAsc } from 'date-fns';
import { BehaviorSubject, catchError, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthResponse, Promoter } from '../../models/interfaces/newAuthResponse';
import { CompanyBlockResponse, CompanyBlocksMap } from '../../models/interfaces/company-block';
import { StorageEntityName } from '../model/storage.model';
import { StorageService } from './storage.service';
import { CacheInterceptor } from '../interceptors/cache.interceptor';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** Datos del usuario */
  user$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  /** Token activo */
  token$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  tokenExpiration$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private http: HttpClient,
    private navCtrl: NavController,
    private router: Router,
    private storage: StorageService,
    private cacheInterceptor: CacheInterceptor,
  ) { }

  /**
   * Ingreso al sistema
   *
   * @param credentials Credenciales del usuario
   */
  login(credentials: {
    email: string;
    password: string;
  }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`auth/login`, { ...credentials }).pipe(
      tap({
        next: async (response) => {
          if (response.access_token) {
            this.token$.next(response.access_token);
            this.user$.next(response.promoter);
            await this.storage.set(StorageEntityName.access_token, response.access_token);
            await this.storage.set(StorageEntityName.tokenExpiration, response.expiration_token);
            await this.storage.set(StorageEntityName.promoter, response.promoter);
            await this.storage.set(StorageEntityName.evidenceTypes, response.image_types);
            await this.storage.set(StorageEntityName.exhibitions, response.exhibitions);
            await this.loadCompanyBlocks(response.promoter);
          }
        },
      })
    );
  }

  /**
   * Descarga los Company Blocks (Productos Presentes) de cada cliente del promotor
   * y los persiste en Storage bajo la llave COMPANY_BLOCKS.
   * Es tolerante a errores por cliente para no bloquear el login en modo offline.
   */
  private async loadCompanyBlocks(promoter: Promoter): Promise<void> {
    const customers = promoter?.customers ?? [];
    if (customers.length === 0) {
      return;
    }

    const requests = customers.map((customer) =>
      this.http.get<CompanyBlockResponse>(`company-block/${customer.id}`).pipe(
        catchError(() => of(null))
      )
    );

    const results = await firstValueFrom(forkJoin(requests));

    const map: CompanyBlocksMap = {};
    customers.forEach((customer, index) => {
      if (results[index]) {
        map[customer.id] = results[index];
      }
    });

    await this.storage.set(StorageEntityName.companyBlocks, map);
  }

  /**
   * Salida del sistema
   */
  logout(): void {
    this.storage.remove(StorageEntityName.access_token);
    this.storage.remove(StorageEntityName.tokenExpiration);
    this.storage.remove(StorageEntityName.promoter);
    this.storage.remove(StorageEntityName.evidenceTypes);
    this.storage.remove(StorageEntityName.exhibitions);
    this.storage.remove(StorageEntityName.companyBlocks);
    this.token$.next(null);
    this.user$.next(null);
    this.cacheInterceptor.clearCache();
    this.navCtrl.navigateRoot('login');
  }

  /**
   * Revisión si existe una sesión de usuario previa alojada en el storage
   */
  async checkPrevSession() {
    /**
     * El token siempre deberá estar definido antes que los datos
     * del usuario
     */
    const token = await this.storage.get<string>(StorageEntityName.access_token);
    this.token$.next(token);
    const user = await this.storage.get<Promoter>(StorageEntityName.promoter);
    this.user$.next(user);
    const tokenExpiration = await this.storage.get<string>(StorageEntityName.tokenExpiration);
    this.tokenExpiration$.next(tokenExpiration);
  }

  /**
   * Mètodo asincrono que devuelve booleano y que comprueba si existe un token
   */
  async token() {
    const token = await this.storage.get<string>(StorageEntityName.access_token);
    if (!token) {
      // await Toast.show({
      //   text: 'Sesión expirada',
      //   duration: 'long',
      //   position: 'top',
      // });
      this.router.navigateByUrl('login');
    }
    return token ? true : false;
  }


  async tokenExpirated() {
    const tokenExpiration = await this.storage.get<string>(StorageEntityName.tokenExpiration);
    return compareAsc(new Date(tokenExpiration), new Date()) === -1;
  }
}
